import { useCallback, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Dialog } from '@base-ui/react';
import {
  BookOpen, PencilSimple, ListChecks,
  PlusSquare, FilePdf, Camera, Books,
  X, Check, CheckCircle, Warning, Paperclip,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import styles from './creer-ameliorer-modal.module.css';

export type CreationKind = 'course' | 'exercice_sheet' | 'exam';
export type CreationSource = 'zero' | 'pdf' | 'photo' | 'existing';

const UPLOAD_SOURCES = new Set<CreationSource>(['pdf', 'photo']);
const ACCEPT_BY_SOURCE: Record<'pdf' | 'photo', string> = {
  pdf: 'application/pdf',
  photo: 'image/jpeg,image/png,image/heic,image/webp',
};

export interface CreerAmeliorerClassroom {
  id: string;
  name: string;
  subject?: string | null;
}

export interface CreerAmeliorerSelection {
  kind: CreationKind;
  source: CreationSource;
  classroomIds: string[];
  note: string;
  attachment: File | null;
}

interface Props {
  classrooms: CreerAmeliorerClassroom[];
  loading?: boolean;
  submitting?: boolean;
  /** Pré-sélectionne un type (ex. bouton « créer un cours » depuis une fiche classe). */
  defaultKind?: CreationKind;
  /** Pré-coche des classes (ex. la classe de la page d'où la modale s'ouvre). */
  defaultClassroomIds?: string[];
  onClose: () => void;
  onSubmit: (selection: CreerAmeliorerSelection) => void;
}

interface Choice<T> {
  value: T;
  label: string;
  hint: string;
  icon: ReactNode;
}

const KINDS: Choice<CreationKind>[] = [
  { value: 'course', label: 'Cours', hint: 'Leçon, contenu', icon: <BookOpen size={17} weight="fill" /> },
  { value: 'exercice_sheet', label: 'Exercice', hint: 'Questions, activités', icon: <PencilSimple size={17} weight="fill" /> },
  { value: 'exam', label: 'Évaluation', hint: 'Note et appréciation', icon: <ListChecks size={17} weight="fill" /> },
];

const SOURCES: Choice<CreationSource>[] = [
  { value: 'zero', label: 'De zéro', hint: 'Page blanche', icon: <PlusSquare size={17} weight="fill" /> },
  { value: 'pdf', label: "D'un PDF", hint: 'Importer un fichier', icon: <FilePdf size={17} weight="fill" /> },
  { value: 'photo', label: "D'une photo", hint: 'Importer une image', icon: <Camera size={17} weight="fill" /> },
  { value: 'existing', label: 'Cours existant', hint: 'Depuis ma bibliothèque', icon: <Books size={17} weight="fill" /> },
];

const SECTION_NAMES = ['la question 1 (type)', 'la question 2 (source)', 'la question 3 (classes)'];

const missingMessage = (missing: number[]) => {
  if (missing.length === 3) return 'Il reste à compléter les 3 questions pour continuer.';
  const names = missing.map((n) => SECTION_NAMES[n - 1]);
  if (names.length === 1) return `Il reste à compléter ${names[0]} pour continuer.`;
  return `Il reste à compléter ${names[0]} et ${names[1]} pour continuer.`;
};

const classLabel = (c: CreerAmeliorerClassroom) => c.name;

function Section({
  num, title, done, error, shaking, innerRef, children,
}: {
  num: string;
  title: string;
  done: string | null;
  error: boolean;
  shaking: boolean;
  innerRef: (el: HTMLDivElement | null) => void;
  children: ReactNode;
}) {
  return (
    <div
      ref={innerRef}
      className={cn(
        'flex flex-col gap-3.5 px-[18px] py-[22px] border-t border-[var(--naia-line)] first:border-t-0 transition-colors',
        error && 'shadow-[inset_0_0_0_2px_var(--naia-error)] bg-[color-mix(in_srgb,var(--naia-error)_6%,transparent)]',
        shaking && styles.shaking,
      )}
    >
      <div className="flex items-start gap-4">
        <span
          className={cn(
            'font-mono text-[38px] font-extrabold leading-none tracking-[-0.02em] shrink-0 min-w-[52px] pt-[3px]',
            error ? 'text-[var(--naia-error)]' : 'text-[var(--naia-blue)]',
          )}
        >
          {num}
        </span>
        <div className="flex flex-col gap-1.5 pt-1.5 min-w-0">
          <span className="text-[17px] font-extrabold leading-[1.25] tracking-[-0.015em] text-[var(--naia-text-primary)]">
            {title}
          </span>
          {error && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--naia-error)]">
              <Warning size={13} weight="fill" /> À compléter
            </span>
          )}
          {!error && done && (
            <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-[var(--naia-green-bg)] px-2.5 py-1">
              <CheckCircle size={12} weight="fill" className="text-[var(--naia-green)] shrink-0" />
              <span className="text-[11.5px] font-bold text-[var(--naia-text-secondary)]">{done}</span>
            </span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function ChoiceTile<T extends string>({ choice, selected, onSelect }: {
  choice: Choice<T>;
  selected: boolean;
  onSelect: (v: T) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(choice.value)}
      className={cn(
        'flex flex-col gap-2 rounded-[var(--naia-r-md)] border-2 p-[13px_11px] text-left min-h-[88px] cursor-pointer',
        'transition-colors bg-[var(--naia-card)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--naia-blue)]',
        selected
          ? 'border-[var(--naia-blue)] bg-[var(--naia-sky)]'
          : 'border-[var(--naia-line)] hover:border-[var(--naia-blue)] hover:bg-[var(--naia-sky)]',
      )}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--naia-info-bg)] text-[var(--naia-blue)]">
        {choice.icon}
      </span>
      <span className="text-[13px] font-bold leading-[1.25] tracking-[-0.01em] text-[var(--naia-text-primary)]">
        {choice.label}
      </span>
      <span className="text-[11.5px] leading-[1.4] text-[var(--naia-text-secondary)]">{choice.hint}</span>
    </button>
  );
}

export default function CreerAmeliorerModal({
  classrooms, loading = false, submitting = false, defaultKind, defaultClassroomIds, onClose, onSubmit,
}: Props) {
  const [kind, setKind] = useState<CreationKind | null>(defaultKind ?? null);
  const [source, setSource] = useState<CreationSource | null>(null);
  const [selectedClasses, setSelectedClasses] = useState<string[]>(defaultClassroomIds ?? []);
  const [note, setNote] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [errors, setErrors] = useState<number[]>([]);
  const [shaking, setShaking] = useState<number[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const setSectionRef = useCallback(
    (n: number) => (el: HTMLDivElement | null) => { sectionRefs.current[n] = el; },
    [],
  );

  const clear = (n: number) => {
    setErrors((prev) => prev.filter((x) => x !== n));
    setMessage(null);
  };

  const toggleClass = (id: string) => {
    setSelectedClasses((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    clear(3);
  };

  const selectSource = (v: CreationSource) => {
    setSource(v);
    if (!UPLOAD_SOURCES.has(v)) setAttachment(null);
    clear(2);
  };

  const pickFile = (e: ChangeEvent<HTMLInputElement>) => {
    setAttachment(e.target.files?.[0] ?? null);
    e.target.value = '';
    clear(2);
  };

  const dirty = kind !== null || source !== null || selectedClasses.length > 0 || note.trim() !== '' || attachment !== null;

  const needsAttachment = source !== null && UPLOAD_SOURCES.has(source);

  const submit = () => {
    const missing: number[] = [];
    if (!kind) missing.push(1);
    if (!source || (needsAttachment && !attachment)) missing.push(2);
    if (selectedClasses.length === 0) missing.push(3);

    const first = missing[0];
    if (first !== undefined) {
      setErrors(missing);
      setMessage(missingMessage(missing));
      setShaking(missing);
      window.setTimeout(() => setShaking([]), 400);
      sectionRefs.current[first]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    onSubmit({
      kind: kind!,
      source: source!,
      classroomIds: selectedClasses,
      note: note.trim(),
      attachment,
    });
  };

  const tryClose = () => {
    if (dirty) setConfirmAbandon(true);
    else onClose();
  };

  const kindDone = kind ? `${KINDS.find((k) => k.value === kind)!.label} sélectionné` : null;
  const sourceDone = source
    ? needsAttachment
      ? attachment
        ? `${SOURCES.find((s) => s.value === source)!.label} · ${attachment.name}`
        : null
      : SOURCES.find((s) => s.value === source)!.label
    : null;
  const classesDone = selectedClasses.length
    ? classrooms.filter((c) => selectedClasses.includes(c.id)).map(classLabel).join(', ')
    : null;

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) tryClose(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(11,42,74,0.44)] backdrop-blur-[6px] pt-[env(safe-area-inset-top)] min-[620px]:items-center">
          <Dialog.Popup
            aria-label="Créer et améliorer"
            className="relative flex w-full max-w-[560px] max-h-[96dvh] flex-col overflow-hidden rounded-t-[20px] bg-[var(--naia-card)] shadow-[var(--naia-shadow-hero)] min-[620px]:max-h-[92dvh] min-[620px]:rounded-[20px]"
          >
            <span className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-[var(--naia-line)] min-[620px]:hidden" />

            <div className="flex shrink-0 items-center gap-3 border-b border-[var(--naia-line)] px-[18px] py-2.5">
              <Dialog.Title className="m-0 flex-1 text-[17px] font-extrabold tracking-[-0.02em] text-[var(--naia-text-primary)]">
                Créer et améliorer
              </Dialog.Title>
              <button
                type="button"
                onClick={tryClose}
                aria-label="Fermer"
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[var(--naia-card-alt)] text-[var(--naia-text-secondary)] transition-colors hover:bg-[var(--naia-line)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--naia-blue)]"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="flex flex-1 flex-col overflow-y-auto">
              <Section
                num="01" title="Que préparez-vous ?" done={kindDone}
                error={errors.includes(1)} shaking={shaking.includes(1)} innerRef={setSectionRef(1)}
              >
                <div className="grid grid-cols-3 gap-2">
                  {KINDS.map((c) => (
                    <ChoiceTile
                      key={c.value} choice={c} selected={kind === c.value}
                      onSelect={(v) => { setKind(v); clear(1); }}
                    />
                  ))}
                </div>
              </Section>

              <Section
                num="02" title="À partir de quoi ?" done={sourceDone}
                error={errors.includes(2)} shaking={shaking.includes(2)} innerRef={setSectionRef(2)}
              >
                <div className="grid grid-cols-2 gap-2">
                  {SOURCES.map((c) => (
                    <ChoiceTile
                      key={c.value} choice={c} selected={source === c.value}
                      onSelect={selectSource}
                    />
                  ))}
                </div>

                {needsAttachment && (
                  <div className="flex flex-col gap-2.5 rounded-[var(--naia-r-md)] border border-[var(--naia-line)] bg-[var(--naia-card-alt)] p-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPT_BY_SOURCE[source as 'pdf' | 'photo']}
                      onChange={pickFile}
                      className="sr-only"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 self-start rounded-full border border-[var(--naia-line)] bg-[var(--naia-card)] px-3 py-2 text-[12.5px] font-semibold text-[var(--naia-blue)] transition-colors hover:bg-[var(--naia-sky)]"
                    >
                      <Paperclip size={14} weight="bold" />
                      {attachment ? 'Changer le fichier' : source === 'pdf' ? 'Choisir un PDF' : 'Choisir une photo'}
                    </button>
                    {attachment && (
                      <span className="text-[12.5px] text-[var(--naia-text-secondary)]">
                        {attachment.name} · {(attachment.size / 1024 / 1024).toFixed(1)} Mo
                      </span>
                    )}
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Une précision pour naia ? (optionnel)"
                      rows={2}
                      className="resize-none text-[13.5px]"
                    />
                  </div>
                )}
              </Section>

              <Section
                num="03" title="Pour quelle classe ?" done={classesDone}
                error={errors.includes(3)} shaking={shaking.includes(3)} innerRef={setSectionRef(3)}
              >
                {loading ? (
                  <p className="text-[13.5px] text-[var(--naia-text-secondary)]">Chargement des classes…</p>
                ) : classrooms.length === 0 ? (
                  <p className="text-[13.5px] text-[var(--naia-text-secondary)]">
                    Aucune classe ne vous est affectée dans cette école.
                  </p>
                ) : (
                  <div className="flex flex-col gap-0.5 overflow-hidden rounded-[var(--naia-r-md)] bg-[var(--naia-line)]">
                    {classrooms.map((c) => {
                      const on = selectedClasses.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={cn(
                            'flex min-h-[52px] cursor-pointer items-center gap-3 px-[15px] py-3.5 transition-colors',
                            on ? 'bg-[var(--naia-sky)]' : 'bg-[var(--naia-card)] hover:bg-[var(--naia-sky)]',
                          )}
                        >
                          <input
                            type="checkbox" checked={on} onChange={() => toggleClass(c.id)}
                            className="sr-only"
                          />
                          <span
                            aria-hidden
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                              on ? 'border-[var(--naia-blue)] bg-[var(--naia-blue)]' : 'border-[var(--naia-line)]',
                            )}
                          >
                            {on && <Check size={12} weight="bold" className="text-[var(--naia-on-primary)]" />}
                          </span>
                          <span className="min-w-0 flex-1 text-[15px] font-semibold leading-[1.2] text-[var(--naia-text-primary)]">
                            {c.name}
                            {c.subject && (
                              <span className="text-[12.5px] font-normal text-[var(--naia-text-secondary)]"> · {c.subject}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </Section>
            </div>

            <div className="flex shrink-0 flex-col gap-2 border-t border-[var(--naia-line)] px-[18px] pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))]">
              <Button
                onClick={submit}
                disabled={submitting}
                className="h-[50px] w-full rounded-full text-[15px] font-bold"
              >
                {submitting ? 'Ouverture…' : 'Commencer'}
              </Button>
              {message && (
                <p role="alert" className="px-1 text-center text-[13.5px] leading-[1.55] text-[var(--naia-error)]">
                  {message}
                </p>
              )}
            </div>

            {confirmAbandon && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-[rgba(11,42,74,0.55)] p-6 backdrop-blur-[2px]">
                <div className="flex w-full max-w-[320px] flex-col gap-4 rounded-[20px] bg-[var(--naia-card)] p-[24px_20px] shadow-[var(--naia-shadow-hero)]">
                  <strong className="text-[15px] font-extrabold text-[var(--naia-text-primary)]">Abandonner ?</strong>
                  <p className="text-[15px] leading-[1.55] text-[var(--naia-text-secondary)]">
                    Vos sélections seront perdues. Voulez-vous quitter cette fenêtre ?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" onClick={() => setConfirmAbandon(false)}
                      className="h-11 flex-1 rounded-full text-[14px] font-semibold"
                    >
                      Continuer
                    </Button>
                    <Button
                      variant="secondary" onClick={onClose}
                      className="h-11 flex-1 rounded-full text-[14px] font-semibold"
                    >
                      Abandonner
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
